/**
 * Stable Device Fingerprint Generator
 *
 * Uses Canvas + WebGL + hardware signals for uniqueness.
 * Does NOT use IP address (unstable) or language (can change).
 *
 * This fingerprint is used for:
 * - Device binding (security)
 * - Session hijacking detection
 * - NOT for tracking users
 */

import { createLogger } from './logger.js';

const log = createLogger('DeviceFingerprint');

// Cache fingerprint in memory (same for session lifetime)
let cachedFingerprint = null;

/**
 * Generate canvas fingerprint (GPU rendering signature)
 * Each GPU/browser combination renders slightly differently
 */
function getCanvasFingerprint() {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 50;
    const ctx = canvas.getContext('2d');

    if (!ctx) return 'canvas-no-context';

    // Draw text with specific styling - creates unique rendering
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(0, 0, 200, 50);
    ctx.fillStyle = '#069';
    ctx.fillText('TheArchitect.ai', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('TheArchitect.ai', 4, 17);

    return canvas.toDataURL();
  } catch (e) {
    return 'canvas-error';
  }
}

/**
 * Get WebGL renderer info (GPU identifier)
 * Very unique - identifies specific GPU model
 */
function getWebGLFingerprint() {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

    if (!gl) return 'webgl-unavailable';

    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    if (!debugInfo) return 'webgl-no-debug';

    const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || '';
    const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || '';

    return `${vendor}|||${renderer}`;
  } catch (e) {
    return 'webgl-error';
  }
}

/**
 * Generate stable device fingerprint
 * Combines multiple signals into SHA-256 hash
 *
 * @returns {Promise<string>} 64-character hex hash
 */
export async function generateDeviceFingerprint() {
  const components = [
    // HIGH ENTROPY (unique per device)
    getCanvasFingerprint(),
    getWebGLFingerprint(),

    // MEDIUM ENTROPY (helps differentiate)
    `${screen.width}x${screen.height}`,
    String(screen.colorDepth || 24),
    String(navigator.hardwareConcurrency || 4),
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown',

    // LOW ENTROPY (stable padding)
    navigator.platform || 'unknown',
    String(navigator.maxTouchPoints || 0)
  ];

  // Create SHA-256 hash of all components
  const fingerprint = components.join('|||');
  const encoder = new TextEncoder();
  const data = encoder.encode(fingerprint);

  try {
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  } catch (e) {
    // Fallback for environments without crypto.subtle
    log.warn('crypto.subtle not available, using fallback');
    return fallbackHash(fingerprint);
  }
}

/**
 * Simple fallback hash for environments without crypto.subtle
 */
function fallbackHash(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  // Convert to 64-char hex string (padded)
  const hex = Math.abs(hash).toString(16);
  return hex.padStart(64, '0').substring(0, 64);
}

/**
 * Get cached fingerprint or generate new one
 * Fingerprint is cached for the session lifetime
 *
 * @returns {Promise<string>} 64-character hex fingerprint
 */
export async function getDeviceFingerprint() {
  if (!cachedFingerprint) {
    cachedFingerprint = await generateDeviceFingerprint();
  }
  return cachedFingerprint;
}

/**
 * Store fingerprint in cookie for OAuth flow
 * Cookie persists through redirect to Google and back
 *
 * Handles both production (cross-subdomain) and development
 */
export function storeDeviceFingerprintCookie(fingerprint) {
  const isProduction = window.location.hostname.includes('theaiarchitect.ai');
  const domain = isProduction ? '; Domain=.theaiarchitect.ai' : '';
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';

  // 10 minutes - enough time for OAuth flow
  document.cookie = `device_fp=${fingerprint}; path=/; max-age=600; SameSite=Lax${secure}${domain}`;
}

/**
 * Clear fingerprint cookie after OAuth completes
 */
export function clearDeviceFingerprintCookie() {
  const isProduction = window.location.hostname.includes('theaiarchitect.ai');
  const domain = isProduction ? '; Domain=.theaiarchitect.ai' : '';

  document.cookie = `device_fp=; path=/; max-age=0${domain}`;
}

/**
 * Clear cached fingerprint (for testing/logout)
 */
export function clearCachedFingerprint() {
  cachedFingerprint = null;
}

export default {
  generateDeviceFingerprint,
  getDeviceFingerprint,
  storeDeviceFingerprintCookie,
  clearDeviceFingerprintCookie,
  clearCachedFingerprint
};
